import { motion } from "framer-motion";
import { ArrowUpRight, Github, Sparkles } from "lucide-react";
import { projects } from "../data/portfolio";
import { SectionHeader } from "./SectionHeader";

const Card = ({ p, i, featured }) => {
  return (
    <motion.a
      href={p.href}
      target="_blank"
      rel="noreferrer"
      data-testid={`project-card-${i}`}
      initial={{ opacity: 0, y: 22 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.55, delay: i * 0.08 }}
      className={`glow-card group relative flex flex-col rounded-xl border border-white/10 bg-[#0d0620]/60 backdrop-blur-md p-6 sm:p-8 overflow-hidden ${
        featured ? "md:col-span-2 md:row-span-2" : ""
      }`}
    >
      {featured && (
        <div className="absolute top-5 right-5 flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-purple-500/15 border border-purple-400/30 text-[10px] font-mono tracking-widest text-purple-200 uppercase">
          <Sparkles size={12} /> Featured
        </div>
      )}

      <div className="font-mono text-[11px] tracking-widest text-purple-400/80 uppercase mb-3">
        {p.year} · Project {String(i + 1).padStart(2, "0")}
      </div>

      <h3
        className={`font-bold text-white tracking-tight ${
          featured ? "text-3xl md:text-4xl" : "text-2xl"
        }`}
      >
        {p.name}
      </h3>
      <p className="mt-1 text-purple-300 text-sm font-medium">{p.tagline}</p>

      <p
        className={`mt-4 text-white/65 leading-relaxed ${
          featured ? "text-base md:text-lg max-w-2xl" : "text-sm"
        }`}
      >
        {p.description}
      </p>

      <div className="mt-6 flex flex-wrap gap-2">
        {p.stack.map((t) => (
          <span
            key={t}
            className="font-mono text-[11px] px-2 py-1 rounded border border-purple-500/20 text-purple-100 bg-purple-500/5"
          >
            {t}
          </span>
        ))}
      </div>

      <div className="mt-8 flex items-center gap-2 text-white/70 group-hover:text-purple-300 transition-colors">
        <Github size={16} />
        <span className="text-sm font-medium">View on GitHub</span>
        <ArrowUpRight
          size={16}
          className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
        />
      </div>
    </motion.a>
  );
};

export const Projects = () => {
  return (
    <section
      id="projects"
      data-testid="section-projects"
      className="relative py-24 md:py-32 px-5 sm:px-8"
    >
      <div className="max-w-6xl mx-auto">
        <SectionHeader
          testId="projects-header"
          index="02 // projects"
          title="Things I've built"
          sub="From RAG-powered multi-agent platforms to deep learning on medical signals — each project shipped end-to-end."
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6 auto-rows-auto">
          {projects.map((p, i) => (
            <Card key={p.name} p={p} i={i} featured={p.featured} />
          ))}
        </div>
      </div>
    </section>
  );
};
